import { useEffect, useState } from "react";
import { BiReset } from "react-icons/bi";
import StockTable from "./Models/Logs_model";
import Loader from "./Loader/Loader";
import Swal from 'sweetalert2';

// const ApiUrl = 'http://localhost:8090';
const ApiUrl = 'https://stockbackend.idea2reality.tech';

const signalOptions = [
    // "Get Current Time Signals",
    "5 Min Operations",
    "15 Min Operations"
];

const popupTypeMap = {
    "Get Current Time Signals": "currentTimeSignals",
    "5 Min Operations": "5MOperations",
    "15 Min Operations": "15MOperations"
};

const signalEndpointMap = {
    "5 Min Operations": "/api/signals/directional-bearish/5m",
    "15 Min Operations": "/api/signals/directional-bearish/15m",
    "Get Current Time Signals": "/api/live-logs/run-live-testing"
};

const intervalOptions = ["5m", "15m", "30m", "1h", "1d"];

const BuySellSignal = () => {
    const [allStocks, setAllStocks] = useState([]);
    const [urls, setUrls] = useState([]);
    const [marketPrices, setMarketPrices] = useState({});
    const [checkedUrls, setCheckedUrls] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);

    const [logResponse, setLogResponse] = useState([]);
    const [currentTimeLogs, setCurrentTimeLogs] = useState([]);
    const [candlesInterval, setCandlesInterval] = useState('15m');
    const [operationInterval, setOperationInterval] = useState([]);
    const [selectedSignal, setSelectedSignal] = useState("Get Current Time Signals");
    const [selectedPopupType, setSelectedPopupType] = useState("");

    const [loader, setLoader] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    let controller;

    useEffect(() => {
        const formattedDate = new Date()
            .toISOString()
            .split("T")[0];
        setSelectedDate(formattedDate);
        const fetchStockList = async () => {
            try {
                const response = await fetch(`${ApiUrl}/api/stock/list`);

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                setAllStocks(data);
                setUrls(data);
            } catch (err) {
                console.error('Stock fetch error:', err.message);
            }
        };
        fetchStockList();
    }, []);

    const handleCheckboxChange = (url) => {
        setCheckedUrls((prev) => {
            const exists = prev.some(item => item.Symbol === url.Symbol);
            if (exists) return prev.filter(item => item.Symbol !== url.Symbol);
            if (prev.length >= 10) {
                alert("⚠️ You can only select up to 10 symbols at a time.");
                return prev;
            }
            return [...prev, url];
        });
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setIsSelectAll(isChecked);
        setCheckedUrls(isChecked ? urls : []);
        // setCheckedUrls(isChecked ? urls.slice(0, 10) : []);
    };

    const handleStockSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (!value) {
            setUrls(allStocks);
        } else {
            const filtered = allStocks.filter(stock =>
                stock.Symbol.toLowerCase().includes(value.toLowerCase()) ||
                stock.StockName.toLowerCase().includes(value.toLowerCase())
            );
            setUrls(filtered);
        }
    };

    const fetchData = async (Symbol) => {
        try {
            const res = await fetch(`${ApiUrl}/api/marketprice-stock/${Symbol}/${candlesInterval}`);
            const data = await res.json();
            const price = data.chart?.result?.[0]?.meta?.regularMarketPrice;
            if (price) {
                setMarketPrices(prev => ({ ...prev, [Symbol]: data.chart.result[0].meta }));
            }
        } catch (error) {
            console.error("Error fetching price:", error.message);
        }
    };

    const handleBacktesting = async () => {
        if (checkedUrls.length === 0) {
            alert("⚠️ Please select at least one symbol.");
            return;
        }

        if (controller) controller.abort();
        controller = new AbortController();

        const allowedSymbolsMapping = {
            "NIFTY": "^NSEI",
            "BANKNIFTY": "%5ENSEBANK",
            "CNXFINANCE": "NIFTY_FIN_SERVICE.NS",
            "MIDCPNIFTY1!": "NIFTY_MID_SELECT.NS",
            "M_M": "M&M.NS",
            "M_MFIN": "M&MFIN.NS"
        };

        const symbols = checkedUrls.map(item => allowedSymbolsMapping[item.Symbol] || `${item.Symbol}.NS`);
        setLoader(true);

        try {
            const res = await fetch(`${ApiUrl}/api/live-logs/run-live-testing`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbols, interval: candlesInterval }),
                signal: controller.signal
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Failed to run backtesting');

            if (data.success) {
                console.log(data);
                setIsPopupOpen(true);
                setCurrentTimeLogs(data);
                if (selectedSignal === 'Get Current Time Signals') {
                    setCurrentTimeLogs(data.data);
                } else {
                    setLogResponse(data);
                }
            }

        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Backtesting error:", error.message);
            }
        } finally {
            setLoader(false);
        }
    };

    const handleSignalFetch = async () => {
        if (checkedUrls.length === 0) {
            // alert("⚠️ Please select at least one symbol.");
            Swal.fire({
                icon: 'error',
                title: 'Signal Fetch Failed',
                text: '⚠️ Please select at least one symbol.',
                confirmButtonColor: '#d33'
            });
            return;
        }
        if (selectedSignal != "Get Current Time Signals") {
            if (!selectedDate) {
                Swal.fire({
                    icon: 'error',
                    title: 'Signal Fetch Failed',
                    text: '⚠️ Please select date.',
                    confirmButtonColor: '#d33'
                });
                return;
            }
        }

        if (selectedSignal == "Get Current Time Signals") {
            if (operationInterval.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Signal Fetch Failed',
                    text: '⚠️ Please select time interval.',
                    confirmButtonColor: '#d33'
                });
                return;
            }
        }
        if (controller) controller.abort();
        controller = new AbortController();

        const allowedSymbolsMapping = {
            "NIFTY": "^NSEI",
            "BANKNIFTY": "%5ENSEBANK",
            "CNXFINANCE": "NIFTY_FIN_SERVICE.NS",
            "MIDCPNIFTY1!": "NIFTY_MID_SELECT.NS",
            "M_M": "M&M.NS",
            "M_MFIN": "M&MFIN.NS"
        };

        const symbols = checkedUrls.map(item => allowedSymbolsMapping[item.Symbol] || `${item.Symbol}.NS`);
        const endpoint = signalEndpointMap[selectedSignal];

        if (!endpoint) {
            alert("⚠️ Invalid signal selected.");
            return;
        }

        setLoader(true);

        try {
            const res = await fetch(`${ApiUrl}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbols, interval: candlesInterval, operationInterval: operationInterval, selectedDate: selectedDate }),
                signal: controller.signal
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Failed to fetch signal data');

            if (data.success) {
                const popupType = popupTypeMap[selectedSignal] || "unknown";
                // console.log('popupTypepopupType',selectedSignal,  data);
                setSelectedPopupType(popupType); // NEW LINE to control popup type
                if (selectedSignal === 'Get Current Time Signals') {
                    setCurrentTimeLogs(data.data);
                } else {
                    setLogResponse(data);
                }
                // setLogResponse(data);
                setIsPopupOpen(true);
            }

        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Signal API error:", error.message);

                Swal.fire({
                    icon: 'error',
                    title: 'Signal Fetch Failed',
                    text: error.message,
                    confirmButtonColor: '#d33'
                });
            }
        } finally {
            setLoader(false);
        }
    };

    const groupSize = 20;
    const groupedUrls = [];
    for (let i = 0; i < urls.length; i += groupSize) {
        groupedUrls.push(urls.slice(i, i + groupSize));
    }

    const handleGroupSelectAll = (group, isChecked) => {
        const updated = isChecked
            ? [...checkedUrls, ...group.filter(g => !checkedUrls.some(c => c.Symbol === g.Symbol))]
            : checkedUrls.filter(item => !group.some(g => g.Symbol === item.Symbol));
        setCheckedUrls(updated);
    };

    const handleOperationCheckboxChange = (value) => {
        setOperationInterval((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };

    // date start ------------------------------------
    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    return loader ? <Loader /> : (
        <>
            <StockTable
                logResponse={logResponse}
                currentTimeLogs={currentTimeLogs}
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                dataBtn={selectedPopupType}
                ApiUrl={ApiUrl}
                candlesInterval={candlesInterval}
            />

            <div className="trading-view-container">
                <div>
                    <div className="strategy-grid">
                        <div className="strategy-item">
                            <b>Strategy Builder</b>
                            <div className="trading-view-timeout mt-1">
                                {/* <label>Set time interval:</label>
                                <select value={candlesInterval} onChange={(e) => setCandlesInterval(e.target.value)}>
                                    <option value="5m">5 min</option>
                                    <option value="15m">15 min</option>
                                    <option value="30m">30 min</option>
                                    <option value="1h">1 hour</option>
                                    <option value="1d">1 Day</option>
                                </select> */}

                                <button
                                    key="currentTimeSignals"
                                    className={selectedSignal === "Get Current Time Signals" ? "trading-start-button" : "trading-stop-button"}
                                    onClick={() => setSelectedSignal("Get Current Time Signals")}
                                >
                                    Get Current Time Signals
                                </button>
                            </div>
                            <div className="time-interval-section container" style={{ marginTop: "16px" }}>
                                {/* First Row: Label */}
                                <div className="row mb-2">
                                    <div className="col">
                                        <label><b>Select Time Interval:</b></label>
                                    </div>
                                </div>

                                {/* Second Row: Checkboxes */}
                                <div className="row">
                                    <div className="col">
                                        <div className="interval-options d-flex flex-wrap gap-2 align-items-center">
                                            {intervalOptions.map((interval) => (
                                                <label key={interval} className="d-flex align-items-center me-3">
                                                    <input
                                                        type="checkbox"
                                                        value={interval}
                                                        checked={operationInterval.includes(interval)}
                                                        onChange={() => handleOperationCheckboxChange(interval)}
                                                        className="me-1"
                                                    />
                                                    {interval}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="trading-view-controls mt-3">
                                <button className="trading-start-button" onClick={handleSignalFetch}>Start</button>
                                <button className="trading-start-button">Stop</button>
                            </div>
                        </div>

                        <div className="strategy-item">
                            <b>Signals</b>
                            <div className="trading-view-controls signal-buttons-grid">
                                {signalOptions.map((label) => (
                                    <button
                                        key={label}
                                        className={selectedSignal === label ? "trading-start-button" : "trading-stop-button"}
                                        onClick={() => setSelectedSignal(label)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            {/* Time Interval Section Below Buttons */}
                            <div className="time-interval-section container" style={{ marginTop: "16px" }}>
                                {/* First Row: Label */}
                                <div className="row mb-2">
                                    <div className="col">
                                        <label><b>Select Date:</b></label>
                                    </div>
                                </div>

                                {/* Second Row: Checkboxes */}
                                <div className="row">
                                    <div className="col-3">
                                        <input
                                            type="date"
                                            value={selectedDate}
                                            onChange={handleDateChange}
                                            className="form-control"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="trading-view-controls mt-3">
                                <button className="trading-start-button" onClick={handleSignalFetch}>Start</button>
                                <button className="trading-start-button">Stop</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="trading-view-file-upload">
                    <h3>List of Stocks</h3>
                    <div className="checkbox-search-container">
                        <label className="select-all">
                            <input
                                type="checkbox"
                                checked={isSelectAll}
                                onChange={handleSelectAll}
                            />
                            Select All <b>({urls?.length ?? 0})</b>
                        </label>

                        <label className="search-label">
                            Search:
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={handleStockSearch}
                                placeholder="Stock Name, Symbol"
                                className="search-input"
                            />
                        </label>
                    </div>

                    {groupedUrls.map((group, groupIdx) => {
                        const isGroupSelected = group.every((url) =>
                            checkedUrls.some((item) => item.Symbol === url.Symbol)
                        );

                        return (
                            <div key={groupIdx} className="stock-group">
                                <label className="select-all">
                                    <input
                                        type="checkbox"
                                        checked={isGroupSelected}
                                        onChange={(e) => handleGroupSelectAll(group, e.target.checked)}
                                    />
                                    Select All (Group {groupIdx + 1})
                                </label>

                                <ul className="trading-view-file-list">
                                    {group.map((url, idx) => (
                                        <li key={idx} className="trading-view-file-item-3">
                                            <label style={{
                                                display: "flex",
                                                gap: "5px",
                                                alignItems: "flex-start",
                                                justifyContent: "space-between"
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checkedUrls.some(item => item.Symbol === url.Symbol)}
                                                    onChange={() => handleCheckboxChange(url)}
                                                />
                                                <div style={{ flexDirection: "column", flexGrow: 1 }}>
                                                    <div>
                                                        <i>{url.StockName} ({url.Symbol})</i>
                                                    </div>
                                                </div>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default BuySellSignal;
